import Immutable from "immutable";
import { useMemo } from "react";

import { type VariableScope } from "constants/variables";
import { type Variable } from "slices/variables/types";
import {
  useCreateVariableMutation,
  useEditVariableMutation,
  useGetVariablesQuery,
} from "slices/variables/variablesApi";

import { VARIABLE_TYPE_LABELS, getVariableType } from "./helpers";
import { VariableRecord } from "./records";
import { type GroupedVariables } from "./types";

interface Props {
  scopes?: readonly VariableScope[];
  skip?: boolean;
  includeRules?: boolean;
  includeActions?: boolean;
}

export const useVariables = ({
  scopes,
  skip,
  includeRules,
  includeActions,
}: Props = {}) => {
  // NOTE: this only gets variables that have already been saved to the db
  // legacy variable code creates VariableRecords in redux before the user saves
  // example: you create a new variable in capture block but haven't saved
  // if you use this hook, you won't have access to that new variable until a save happens

  const scopesSerialized = scopes ? scopes.join(",") : undefined;
  const { data: variables, refetch } = useGetVariablesQuery(
    { scope: scopesSerialized, includeRules, includeActions },
    { skip },
  );
  const [createVariable] = useCreateVariableMutation();
  const [editVariable] = useEditVariableMutation();

  const variablesLegacy = useMemo(
    () =>
      variables
        ? Immutable.List(
            variables.map(
              (v) =>
                new VariableRecord({
                  id: v._id,
                  name: v.name,
                  scope: v.scope,
                }),
            ),
          )
        : Immutable.List<VariableRecord>(),
    [variables],
  );

  return {
    variables,
    variablesLegacy,
    refetch,
    createVariable,
    editVariable,
  };
};

export const useFilteredVariables = (
  availableVariables: Variable[] | undefined,
  search: string,
): GroupedVariables => {
  return useMemo(() => {
    const filtered = (availableVariables || []).filter((v) =>
      v.name.toLowerCase().includes(search.toLowerCase()),
    );

    // group variables according to scope
    const groupedMap = Object.keys(VARIABLE_TYPE_LABELS).reduce(
      (acc, key) => ({ ...acc, [key]: [] }),
      {} as GroupedVariables,
    );

    for (const variable of filtered) {
      const group = getVariableType(variable);
      groupedMap[group].push(variable);
    }

    // sort variables within each group alphabetically
    Object.keys(groupedMap).forEach((groupKey) => {
      groupedMap[groupKey as keyof GroupedVariables].sort((a, b) =>
        a.name.localeCompare(b.name),
      );
    });

    return groupedMap;
  }, [search, availableVariables]);
};
